"use client";

import { useTheme } from "next-themes";
import { useEffect } from "react";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("components/providers/native-theme-sync");

/**
 * Must match `--color-background` in globals.css (`@theme` for light, `.dark`
 * for dark). The splash colours (#FFF7DB / #0B0B0F) are a different pair: they
 * belong to the native launch surface, which is gone by the time this runs.
 */
const THEME_COLORS = {
  dark: "#000000",
  light: "#F7F8FB",
} as const;

const isNativePlatform = (): boolean => {
  if (typeof window === "undefined") return false;
  return !!(
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.();
};

/**
 * Point the platform chrome at the theme the app is actually showing.
 *
 * The theme follows the device by default ("system") and can be pinned to
 * light or dark in Settings → Appearance. `resolvedTheme` is always the
 * concrete result — the OS appearance when following the system (next-themes
 * re-resolves it live when the OS flips) or the pinned choice — so every sync
 * below keys off it and handles both cases:
 *
 *   - `<meta name="theme-color">`: the static tags in the root layout carry
 *     `prefers-color-scheme` media queries, which are right for "system" but
 *     wrong for a pinned theme that disagrees with the OS. A media-less tag
 *     wins over the media-scoped ones, so writing one here covers both.
 *   - The iOS keyboard inherits the shell's interface style (the OS
 *     appearance); `Keyboard.setStyle` matches it to a pinned theme.
 *   - Status-bar ICONS: Capacitor's SystemBars default follows the OS, which
 *     is invisible under a pinned theme that disagrees with it (light icons
 *     on a light page). `SystemBars.setStyle` pins them to what is on screen.
 *     It is a Capacitor core plugin, so every installed Capacitor 8 binary
 *     has it.
 *
 * Binaries built before the shell stopped pinning `UIUserInterfaceStyle=Dark`
 * (iOS) report a dark `prefers-color-scheme` whatever the phone uses, so on
 * them "system" resolves dark until the store update lands — the same look
 * they had before, never a broken one.
 */
export function NativeThemeSync() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    // Undefined until next-themes has read storage; acting early would flash
    // the default theme's chrome and then correct itself.
    if (!resolvedTheme) return;

    const isLight = resolvedTheme === "light";
    const color = isLight ? THEME_COLORS.light : THEME_COLORS.dark;

    let meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]:not([media])',
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = color;

    if (!isNativePlatform()) return;

    let cancelled = false;
    void (async () => {
      try {
        const { SystemBars, SystemBarsStyle } = await import("@capacitor/core");
        if (cancelled) return;
        // LIGHT = dark icons for a light page; DARK = light icons.
        await SystemBars.setStyle({
          style: isLight ? SystemBarsStyle.Light : SystemBarsStyle.Dark,
        });
      } catch (error) {
        logger.debug("[Theme] system bar style sync skipped", error);
      }
      try {
        const { Keyboard, KeyboardStyle } = await import("@capacitor/keyboard");
        if (cancelled) return;
        await Keyboard.setStyle({
          style: isLight ? KeyboardStyle.Light : KeyboardStyle.Dark,
        });
      } catch (error) {
        // Android has no equivalent and older iOS can reject the call; a
        // mismatched keyboard tint must never break the app.
        logger.debug("[Theme] keyboard style sync skipped", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedTheme]);

  return null;
}
